import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { KnowledgeLibraryFilterPanel } from '../components/knowledge-library/knowledge-library-filter-panel'
import {
  KnowledgeLibraryHero,
  KnowledgeLibraryLoadedSummary,
  KnowledgeLibraryResultsHeader,
} from '../components/knowledge-library/knowledge-library-page-sections'
import { KnowledgeLibraryRow } from '../components/knowledge-library/knowledge-library-row'
import {
  KnowledgeLibraryEmpty,
  KnowledgeLibraryError,
  KnowledgeLibrarySkeleton,
} from '../components/knowledge-library/knowledge-library-states'
import { useAuth } from '../hooks/use-auth'
import { buildCreateDocumentBody, useCreateDocument, useDocuments } from '../hooks/use-documents'
import { canEditDocument, documentEditPath } from '../lib/document-route'
import {
  buildActiveSourceOverview,
  buildKnowledgeLibraryState,
  buildMemoryClassFacets,
} from '../lib/knowledge-library-state'
import {
  buildKnowledgeLibraryReturnState,
  DEFAULT_LIBRARY_QUERY_STATE,
  parseLibraryQueryState,
  serializeLibraryQueryState,
  updateLibraryQueryState,
  type LibraryQueryState,
} from '../lib/knowledge-library-query-state'
import { returnLocationKey, useRestoreDocumentReturn } from '../lib/document-return-restoration'
import { useAuthorityExpiryRefresh } from '../hooks/use-authority-expiry-refresh'
import { getDocumentSourceIdentity, sourceIdentityMatches } from '../lib/document-source-identity'

const viewLabels: Record<LibraryQueryState['view'], string> = {
  all: 'All knowledge',
  'needs-review': 'Needs review',
  decisions: 'Decisions',
  runbooks: 'Runbooks',
  handoffs: 'Handoffs',
}

export function DocsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const { role } = useAuth()
  const createDocument = useCreateDocument()
  const documents = useDocuments({ limit: 100, sort: 'updatedAt', order: 'desc' })
  const loadedDocs = documents.data?.data ?? []
  const authorityExpiryRevision = useAuthorityExpiryRefresh(
    loadedDocs.map((doc) => doc.sourceProvenance?.authorityExpiresAt),
  )
  const queryState = useMemo(() => parseLibraryQueryState(searchParams), [searchParams])
  const canonicalQueryState = useMemo(() => {
    // Preserve a legacy selector only while its candidate sources are loading.
    // It is never serialized during that window, and is replaced with an opaque
    // fingerprint (or removed) as soon as the loaded tenant data can resolve it.
    if (queryState.source === 'all' || documents.isLoading) return queryState
    const matchedSource = loadedDocs
      .map((doc) => doc.sourceProvenance)
      .find((source) => sourceIdentityMatches(source, queryState.source))
    return {
      ...queryState,
      source: matchedSource ? getDocumentSourceIdentity(matchedSource).key : 'all',
    }
  }, [documents.isLoading, loadedDocs, queryState])
  const state = useMemo(
    () => buildKnowledgeLibraryState(loadedDocs, canonicalQueryState),
    [authorityExpiryRevision, canonicalQueryState, loadedDocs],
  )
  const memoryClassFacets = useMemo(() => buildMemoryClassFacets(loadedDocs), [loadedDocs])
  const sourceOverview = useMemo(
    () => buildActiveSourceOverview(loadedDocs, canonicalQueryState.source),
    [canonicalQueryState.source, loadedDocs],
  )
  const returnState = useMemo(() => buildKnowledgeLibraryReturnState(searchParams), [searchParams])
  const tenantTotal = documents.data?.total ?? loadedDocs.length
  const canCreateManual = role === 'admin' || role === 'editor'
  useEffect(() => {
    if (canonicalQueryState.source === queryState.source) return
    setSearchParams(serializeLibraryQueryState(canonicalQueryState), { replace: true })
  }, [canonicalQueryState, queryState.source, setSearchParams])
  useRestoreDocumentReturn({
    locationKey: returnLocationKey(location.pathname, location.search),
    ready: !documents.isLoading && !documents.isError,
    scrollRef,
    fallbackRef: resultsHeadingRef,
  })

  const updateState = (updates: Partial<LibraryQueryState>) => {
    setSearchParams(updateLibraryQueryState(searchParams, updates), { replace: true })
  }

  const clearFilters = () => {
    setSearchParams(serializeLibraryQueryState({ ...DEFAULT_LIBRARY_QUERY_STATE }), {
      replace: true,
    })
  }

  const createManualDocument = async () => {
    try {
      const doc = await createDocument.mutateAsync(
        buildCreateDocumentBody({
          title: 'Untitled',
          source: 'the Knowledge library',
        }),
      )
      navigate(documentEditPath(doc), { state: returnState })
    } catch (error) {
      toast.error('Manual document creation failed', {
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  const countSummary = `${state.stats.matching} matching ${
    state.stats.matching === 1 ? 'result' : 'results'
  } from ${state.stats.loaded} loaded`
  return (
    <div className="aw-knowledge-page" ref={scrollRef} data-return-scroll-container>
      <div className="aw-knowledge-main">
        <KnowledgeLibraryHero
          query={canonicalQueryState.query}
          onQueryChange={(query) => updateState({ query })}
        />

        {documents.isLoading ? (
          <section className="aw-knowledge-request-state" aria-label="Knowledge documents">
            <KnowledgeLibrarySkeleton />
          </section>
        ) : documents.isError ? (
          <section className="aw-knowledge-request-state" aria-label="Knowledge documents">
            <KnowledgeLibraryError retry={() => void documents.refetch()} />
          </section>
        ) : (
          <>
            <KnowledgeLibraryLoadedSummary
              stats={state.stats}
              workspaceTotal={tenantTotal}
              sourceOverview={sourceOverview}
            />

            <section className="aw-knowledge-layout">
              <KnowledgeLibraryFilterPanel
                state={canonicalQueryState}
                sourceFacets={state.sourceFacets}
                memoryClassFacets={memoryClassFacets}
                authorityAvailable={state.authorityAvailable}
                updateState={updateState}
              />

              <section className="aw-knowledge-results" aria-label="Knowledge documents">
                <KnowledgeLibraryResultsHeader
                  headingRef={resultsHeadingRef}
                  countSummary={countSummary}
                  title={viewLabels[canonicalQueryState.view]}
                  canCreateManual={canCreateManual}
                  isCreating={createDocument.isPending}
                  onCreateManual={() => void createManualDocument()}
                />

                {canonicalQueryState.view === 'needs-review' && !state.authorityAvailable && (
                  <p className="aw-knowledge-authority-unavailable" role="status">
                    Needs review is unavailable until human-governed authority metadata is present.
                  </p>
                )}
                {state.filteredDocs.length ? (
                  <div className="aw-knowledge-list">
                    {state.filteredDocs.map((doc) => (
                      <KnowledgeLibraryRow
                        key={doc.id}
                        doc={doc}
                        canEdit={canEditDocument(doc, role)}
                        returnState={returnState}
                        role={role}
                        originLocationKey={returnLocationKey(location.pathname, location.search)}
                      />
                    ))}
                  </div>
                ) : (
                  <KnowledgeLibraryEmpty
                    workspaceEmpty={tenantTotal === 0}
                    loadedCount={loadedDocs.length}
                    onCreateManual={canCreateManual ? () => void createManualDocument() : undefined}
                    onClearFilters={clearFilters}
                  />
                )}
              </section>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
